import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  MouseEvent,
  useState,
  useRef,
} from 'react';
import * as Styled from '../styled';
import { Cursors, resizeData } from '../types';

const CURSOR_CAPTURE_TIMEOUT_MS = 150;
let timeout: NodeJS.Timeout;

export const MouseEventsContainer = ({
  children,
  style,
  onMouseEnterDo,
  onMouseLeaveDo,
  onMouseDownDo,
  setResizeW,
  setResizeH,
  id,
  w,
  h,
  resizePaddings,
}: {
  children?: JSX.Element | JSX.Element[];
  style?: CSSProperties;
  onMouseEnterDo?: () => void;
  onMouseLeaveDo?: () => void;
  onMouseDownDo: () => void;
  setResizeW: Dispatch<SetStateAction<resizeData | undefined>>;
  setResizeH: Dispatch<SetStateAction<resizeData | undefined>>;
  id: string;
  w: number;
  h: number;
  resizePaddings: number;
}) => {
  const mouseEventsContainerRef = useRef(null);
  const [isMouseInResizeField, setIsMouseInResizeField] = useState(false);
  const [cursor, setCursor] = useState<Cursors>('inherit');

  const getCoords = () => {
    const rect = (
      mouseEventsContainerRef.current! as HTMLDivElement
    ).getBoundingClientRect();

    const Xtill = rect.x + resizePaddings;
    const Xfrom = rect.x + rect.width - resizePaddings;
    const Ytill = rect.y + resizePaddings;
    const Yfrom = rect.y + rect.height - resizePaddings;

    return { Xtill, Xfrom, Ytill, Yfrom };
  };

  const resolveCursor = (event: MouseEvent<HTMLElement>) => {
    if (!isMouseInResizeField) {
      if (cursor) setCursor('inherit');
      return;
    }

    const { Xtill, Xfrom, Ytill, Yfrom } = getCoords();

    if (event.clientY < Ytill && event.clientX < Xtill) setCursor('nwse-resize');
    if (event.clientY > Yfrom && event.clientX > Xfrom) setCursor('nwse-resize');

    if (event.clientY < Ytill && event.clientX > Xfrom) setCursor('nesw-resize');
    if (event.clientY > Yfrom && event.clientX < Xtill) setCursor('nesw-resize');

    if (event.clientX > Xtill && event.clientX < Xfrom) setCursor('ns-resize');

    if (event.clientY > Ytill && event.clientY < Yfrom) setCursor('ew-resize');
  };

  const startEventsContainerMove = (event: MouseEvent<HTMLElement>) => {
    if (!isMouseInResizeField) return;
    if (!mouseEventsContainerRef.current) return;

    const { Xtill, Xfrom, Ytill, Yfrom } = getCoords();

    if (event.clientY < Ytill) {
      setResizeH({ movement: event.clientY, direction: -1 });
    }

    if (event.clientY > Yfrom) {
      setResizeH({ movement: event.clientY, direction: 1 });
    }

    if (event.clientX < Xtill) {
      setResizeW({ movement: event.clientX, direction: -1 });
    }

    if (event.clientX > Xfrom) {
      setResizeW({ movement: event.clientX, direction: 1 });
    }
  };

  const mouseInOverResizeFieldDebounce = () => {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      setIsMouseInResizeField(true);
    }, CURSOR_CAPTURE_TIMEOUT_MS);
  };

  const mouseInOutResizeFieldDebounce = () => {
    if (timeout) clearTimeout(timeout);
    setIsMouseInResizeField(false);
  };

  return (
    <Styled.MouseEventsContainer
      ref={mouseEventsContainerRef}
      id={`mouse-events-container-${id}`}
      padding={resizePaddings / 2}
      style={{ ...style, cursor }}
      onMouseEnter={() => {
        mouseInOverResizeFieldDebounce();
        onMouseEnterDo?.();
      }}
      onMouseLeave={() => {
        mouseInOutResizeFieldDebounce();
        onMouseLeaveDo?.();
      }}
      onMouseDown={(event) => {
        startEventsContainerMove(event);
        onMouseDownDo();
      }}
      onMouseMove={resolveCursor}
    >
      <Styled.Container
        style={{ width: `${w}px`, height: `${h}px` }}
        onMouseEnter={mouseInOutResizeFieldDebounce}
        onMouseLeave={mouseInOverResizeFieldDebounce}
      >
        {children}
      </Styled.Container>
    </Styled.MouseEventsContainer>
  );
};
